import { Button, Css } from "@homebound/beam";
import { useCallback } from "react";
import { useRouteParams } from "src/hooks/useRouteParams";
import { PageHeader } from "src/routes/layout/PageHeader";
import { useDesignPackageVersionHistoryQuery } from "src/routes/libraries/design-catalog/version-history/DesignPackageVersionHistoryPage.generated";
import { VersionDetailsCard_VersionFragment } from "src/routes/libraries/versioning/VersionDetailsCard.generated";
import { createDesignPackageUrl, createDesignPackagesUrl } from "src/RouteUrls";
import { fail, queryResult } from "src/utils";
import { VersionDetailsCard } from "../../versioning/VersionDetailsCard";
import { ItemSlotUpdates } from "./components/ItemSlotUpdates";
import { ProductUpdates } from "./components/ProductUpdates";

type DesignPackageVersionHistoryUrlParams = { designPackageId: string };

export function DesignPackageVersionHistoryPage() {
  const { designPackageId } = useRouteParams<DesignPackageVersionHistoryUrlParams>();
  const query = useDesignPackageVersionHistoryQuery({
    variables: { filter: { readyPlanId: [designPackageId] }, page: { limit: 10 } },
  });
  const maybeFetchMore = useCallback(async () => {
    if (query.data?.readyPlanAggregateVersionsPage.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: query.data?.readyPlanAggregateVersionsPage.entities?.length, limit: 10 } },
      });
    }
  }, [query]);

  return queryResult(query, ({ readyPlanAggregateVersionsPage }) => (
    <DesignPackageVersionHistoryView
      versions={readyPlanAggregateVersionsPage.entities}
      hasNextPage={readyPlanAggregateVersionsPage.pageInfo.hasNextPage}
      maybeFetchMore={maybeFetchMore}
    />
  ));
}

type DesignPackageVersionHistoryViewProps = {
  versions: VersionDetailsCard_VersionFragment[];
  hasNextPage: boolean;
  maybeFetchMore: () => Promise<void>;
};

function DesignPackageVersionHistoryView(props: DesignPackageVersionHistoryViewProps) {
  const { versions, hasNextPage, maybeFetchMore } = props;
  const { designPackageId } = useRouteParams<DesignPackageVersionHistoryUrlParams>();
  // We reverse the array so that the most recent version is shown first.
  const rpavs = [...versions].reverse();
  const latestVersion = rpavs.first ?? fail("Expected at least one version");

  return (
    <>
      <PageHeader
        title="Version History"
        breadcrumb={[
          { href: createDesignPackagesUrl(), label: "Design Packages" },
          { href: createDesignPackageUrl(designPackageId, latestVersion.id), label: latestVersion.readyPlan.name },
        ]}
      />
      <div css={Css.df.fdc.gap2.$}>
        {rpavs.reverse().map((rpav) => (
          <VersionDetailsCard
            key={rpav.id}
            version={rpav}
            changeDetails={<DesignPackageVersionChanges version={rpav} />}
            versionUrl={createDesignPackageUrl(rpav.readyPlan.id, rpav.id)}
          />
        ))}
      </div>
      {hasNextPage && (
        <div css={Css.df.jcc.mt1.mb1.p2.$}>
          <Button label="Load More" onClick={maybeFetchMore} />
        </div>
      )}
    </>
  );
}

type DesignPackageVersionChangesProps = { version: VersionDetailsCard_VersionFragment };

function DesignPackageVersionChanges({ version }: DesignPackageVersionChangesProps) {
  return (
    <>
      <ItemSlotUpdates tliVersions={version.tliVersions} />
      <ProductUpdates tliVersions={version.tliVersions} />
    </>
  );
}
